/**
 * インフォグラフィック型ポスター
 *
 * 1 日ごとの滞在帯・ルート・写真・数字を 1 枚に詰め込み、後から読み返せる資料にする。
 */

import type { TripDay } from '@/api/types/trip'
import { parseUtcDate } from '@/lib/date-utils'
import { shortPrefectureName } from '@/lib/prefectures'

import { dateRangeLabel, durationLabel } from '../trip-utils'
import type { PosterInput } from './poster-canvas'
import {
  BG,
  drawRoundedCover,
  drawRoute,
  drawStats,
  fitLines,
  FONT,
  HEIGHT,
  MARGIN,
  roundedRect,
  statItems,
  SUBTLE,
  TEXT,
  WIDTH,
  withAlpha,
} from './poster-canvas'

/** ポスターに載せる最大日数 */
const MAX_DAY_ROWS = 5
const ROW_HEIGHT = 76
const BAR_HEIGHT = 18
const DAY_MS = 24 * 60 * 60 * 1000

/** JST の 0 時を基準に、その日の何割の位置かを返す */
const dayFraction = (dayStartMs: number, iso: string) => {
  const ratio = (parseUtcDate(iso).getTime() - dayStartMs) / DAY_MS
  return Math.min(Math.max(ratio, 0), 1)
}

const drawDayBar = (
  ctx: CanvasRenderingContext2D,
  day: TripDay,
  dayNumber: number,
  dayLine: string,
  top: number,
  accent: string,
) => {
  ctx.fillStyle = accent
  ctx.font = FONT(24, 700)
  ctx.fillText(`${dayNumber}日目`, MARGIN, top + 22)

  ctx.fillStyle = SUBTLE
  ctx.font = FONT(24)
  ctx.fillText(dayLine, MARGIN + 110, top + 22)

  ctx.textAlign = 'right'
  ctx.fillStyle = SUBTLE
  ctx.font = FONT(22)
  ctx.fillText(`${day.steps.toLocaleString()} 歩`, WIDTH - MARGIN, top + 22)
  ctx.textAlign = 'left'

  const barY = top + 38
  const barW = WIDTH - MARGIN * 2
  roundedRect(ctx, MARGIN, barY, barW, BAR_HEIGHT, BAR_HEIGHT / 2)
  ctx.fillStyle = 'rgba(148, 163, 184, 0.18)'
  ctx.fill()

  const dayStartMs = Date.parse(`${day.date}T00:00:00+09:00`)
  if (Number.isNaN(dayStartMs)) return

  for (const visit of day.visits) {
    if (!visit.arrival_date || !visit.departure_date) continue
    const from = dayFraction(dayStartMs, visit.arrival_date)
    const to = dayFraction(dayStartMs, visit.departure_date)
    if (to <= from) continue
    const x = MARGIN + barW * from
    // 一瞬の滞在でも見えるように最小幅を確保する
    const w = Math.max(barW * (to - from), 4)
    roundedRect(ctx, x, barY, w, BAR_HEIGHT, BAR_HEIGHT / 2)
    ctx.fillStyle = accent
    ctx.fill()
  }
}

export const renderInfographicPoster = (ctx: CanvasRenderingContext2D, input: PosterInput) => {
  const { trip, accent, days, dayLines, headline, routePoints, filmstrip } = input

  ctx.fillStyle = BG
  ctx.fillRect(0, 0, WIDTH, HEIGHT)

  // ── ヘッダー ──────────────────────────────
  ctx.fillStyle = accent
  ctx.font = FONT(26, 700)
  ctx.fillText(durationLabel(trip), MARGIN, MARGIN + 30)

  const title = fitLines(ctx, trip.title, WIDTH - MARGIN * 2, 1, 58, 32)
  ctx.fillStyle = TEXT
  ctx.font = FONT(title.size, 700)
  ctx.fillText(title.lines.at(0) ?? trip.title, MARGIN, MARGIN + 90)

  ctx.fillStyle = SUBTLE
  ctx.font = FONT(24)
  const prefLabel = trip.stats.prefectures.map(shortPrefectureName).join(' · ')
  ctx.fillText(`${dateRangeLabel(trip)}${prefLabel ? `\u3000${prefLabel}` : ''}`, MARGIN, MARGIN + 130)

  const copy = fitLines(ctx, headline, WIDTH - MARGIN * 2, 2, 40, 26)
  let cursorY = MARGIN + 186
  for (const line of copy.lines) {
    ctx.fillStyle = TEXT
    ctx.font = FONT(copy.size, 700)
    ctx.fillText(line, MARGIN, cursorY)
    cursorY += copy.size * 1.2
  }

  // ── 日ごとの滞在帯 ──────────────────────────
  cursorY += 24
  days.slice(0, MAX_DAY_ROWS).forEach((day, i) => {
    drawDayBar(ctx, day, i + 1, dayLines.at(i) ?? '', cursorY + ROW_HEIGHT * i, accent)
  })
  cursorY += ROW_HEIGHT * Math.min(days.length, MAX_DAY_ROWS) + 24

  // ── ルート ────────────────────────────────
  const filmHeight = filmstrip.length > 0 ? 132 : 0
  const statsTop = HEIGHT - MARGIN - 96
  const routeHeight = statsTop - filmHeight - cursorY - 40
  if (routeHeight > 120) {
    roundedRect(ctx, MARGIN, cursorY, WIDTH - MARGIN * 2, routeHeight, 24)
    ctx.fillStyle = withAlpha(accent, 0.06)
    ctx.fill()
    drawRoute(
      ctx,
      routePoints,
      { x: MARGIN + 24, y: cursorY + 24, w: WIDTH - MARGIN * 2 - 48, h: routeHeight - 48 },
      accent,
    )
    cursorY += routeHeight + 20
  }

  // ── フィルムストリップ ────────────────────────
  if (filmstrip.length > 0) {
    const gap = 10
    const count = Math.min(filmstrip.length, 6)
    const thumbW = (WIDTH - MARGIN * 2 - gap * (count - 1)) / count
    filmstrip.slice(0, count).forEach((bitmap, i) => {
      drawRoundedCover(ctx, bitmap, MARGIN + (thumbW + gap) * i, cursorY, thumbW, 112, 12)
    })
  }

  drawStats(ctx, statItems(trip), statsTop)
}
